import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

/**
 * Privacy Policy — APPROVED FINAL (May 26, 2026).
 *
 * US-tailored with explicit GDPR / CCPA / COPPA accommodations because
 * we accept payments from the EU and California. This document is the
 * living source of truth — when sub-processors or data flows change,
 * update this file and bump `lastUpdated`.
 *
 * Section numbering follows the published copy — do not renumber
 * without legal review. Note that there are two §2.3 subsections by
 * design (§2.3 Payments and §2.3a Armstrong Transport-specific data).
 */
export default function PrivacyPolicy() {
  return (
    <LegalLayout
      slug="privacy"
      eyebrow="Policy"
      title="Privacy Policy"
      lastUpdated="June 5, 2026"
    >
      <div
        data-testid="legal-dba-disclosure"
        className="text-[12px] text-[#6B6B6B] border-l-2 border-gold/40 pl-3 my-4 not-prose"
      >
        Armstrong K9 Registry is a trade name of <strong>Armstrong K9 Registry
        LLC</strong>, a California limited liability company (registered agent
        address: 4054 Highway 273, Anderson, CA 96007). References to
        &ldquo;Armstrong K9 Registry&rdquo; in this document mean Armstrong
        K9 Registry LLC and its successors and assigns.
      </div>
      <p>
        Armstrong K9 Registry (&ldquo;we&rdquo;, &ldquo;our&rdquo;,
        &ldquo;the Registry&rdquo;) operates a dog pedigree, breeder, and
        marketplace platform. This Privacy Policy describes what personal
        data we collect, how we use it, who we share it with, and the rights
        you have over your information.
      </p>

      <h2>1. Who is the data controller</h2>
      <p>
        Armstrong K9 Registry is the controller of personal data about its
        account holders. For visitors who use only public pages (browsing
        kennels, dog profiles, the marketplace) we are also the controller
        for log and analytics data.
      </p>
      <p>
        Contact our privacy team at{" "}
        <a href="mailto:privacy@armstrongk9registry.com">
          privacy@armstrongk9registry.com
        </a>{" "}
        for any privacy question or to exercise your rights.
      </p>

      <h2>2. What we collect</h2>

      <h3>2.1 Account &amp; profile</h3>
      <ul>
        <li>Name and email address (required to register).</li>
        <li>
          Date of birth (used to verify you are at least 18; we do not store
          full DOB after verification &mdash; only an &ldquo;is_18+&rdquo;
          flag).
        </li>
        <li>Optional kennel name, bio, slug, public photos.</li>
        <li>
          Notification preferences (heat reminders, listing expiry,
          broadcasts).
        </li>
      </ul>

      <h3>2.2 Pet &amp; pedigree records</h3>
      <ul>
        <li>
          Dog name, breed, sex, date of birth, color, microchip number,
          registration number.
        </li>
        <li>
          Health records, titles, awards, photographs, ownership history,
          pedigree (sire/dam) links.
        </li>
        <li>
          Litter records, breeding listings, heat-cycle data (for females).
        </li>
      </ul>

      <h3>2.3 Payments</h3>
      <ul>
        <li>
          Card payments are processed by Stripe; we never store full card
          numbers.
        </li>
        <li>
          We retain order metadata: amount, currency, package, Stripe session
          ID, paid timestamp.
        </li>
        <li>
          Payouts to breeders flow through Stripe Connect Express; bank
          routing data is held by Stripe under their PCI-DSS attestation.
        </li>
        <li>
          Armstrong Transport bookings use the same Stripe Connect Express
          pipeline &mdash; transporter bank routing information, tax
          identification, and identity verification documents are submitted
          directly to Stripe and never touch Registry servers. We retain only
          the transporter&rsquo;s Stripe Connect account ID, payouts-enabled
          flag, and last verification timestamp.
        </li>
      </ul>

      <h3>2.3a Armstrong Transport (transporter-specific data)</h3>
      <ul>
        <li>
          <strong>Identity &amp; licensing:</strong> USDA Class B transport
          license number, commercial-auto insurance carrier + policy number
          + liability limit + expiry date. We retain a copy on file for
          verification and audit; these fields are <strong>NEVER shown
          publicly</strong> on the directory or profile pages.
        </li>
        <li>
          <strong>Service area:</strong> The U.S. state codes you cover and
          (optionally) preferred lanes, breed-size familiarity, blackout
          dates, and minimum/maximum trip distance. These appear on your
          public profile and drive the routing engine.
        </li>
        <li>
          <strong>Trip metadata:</strong> Pickup/dropoff city + state, dog
          count, breed size, requested dates, escrow status, delay flags,
          dispute history. Buyer email is bound to the order and visible to
          the assigned transporter but never on the public directory.
        </li>
        <li>
          <strong>Repeat-business pairs:</strong> When a trip completes, a{" "}
          <code>preferred_transporter_pairs</code> record links the
          buyer&rsquo;s email to the transporter&rsquo;s id with a trip
          counter. This drives the &ldquo;Preferred clients&rdquo; view in
          your dashboard and gives repeat buyers first-look routing in future
          Phase 7 features. Either party may request mute or deletion of a
          pair at any time.
        </li>
      </ul>

      <h3>2.4 Communications</h3>
      <ul>
        <li>
          In-app inbox messages, marketplace inquiries, chat threads,
          breeding interest notes.
        </li>
        <li>
          Email content we send via Resend (transactional notifications,
          certificate attachments, transfer invites).
        </li>
        <li>
          Marketing announcements (e.g. feature-launch blasts) are delivered
          both via in-app inbox and email. You can opt out of broadcasts at
          any time from Settings &rarr; Notification preferences; opting out is
          honored before we send.
        </li>
        <li>
          <strong>Lifecycle emails about your dog(s).</strong> If you own a
          registered dog on the platform, we send a small set of
          date-triggered emails: an annual <em>birthday</em> note, an annual{" "}
          <em>Gotcha Day</em> anniversary note (one year after a transfer),
          a one-time 30-day <em>buyer check-in</em> after you receive a dog
          (which invites you to leave a verified review for the seller),
          and &mdash; if you bought a puppy &mdash; a six-lesson{" "}
          <em>first-puppy school drip</em> on days 1, 3, 5, 7, 14, and 30
          after homecoming. Every lifecycle email contains a one-click
          unsubscribe link (CAN-SPAM Section 5(a)(3) compliant) AND can be
          managed globally from Settings &rarr; Notifications or
          per-dog from each dog's Ownership tab. Transactional emails
          (purchases, password resets, transfer requests, certificate
          delivery) cannot be unsubscribed from because they are required
          to operate the service. The lifecycle email content is
          educational only; it is not veterinary, legal, or tax advice.
        </li>
      </ul>

      <h3>2.4a Provider &harr; Client direct messaging</h3>
      <p>
        When you use the in-app messaging system between a service
        provider (trainer, groomer, walker, sitter) and a client
        ("Provider&nbsp;&harr;&nbsp;Client Messaging"), the following
        applies:
      </p>
      <ul>
        <li>
          <strong>What we store.</strong> Every message body, photo
          attachment, system event (e.g.&nbsp;automated booking
          confirmations), sender identity, timestamp, and read
          receipt is stored on the Registry. Photo attachments are
          stored in the same encrypted object store as your other
          uploads. Messages are stored as plaintext on our database
          and our backups, encrypted at rest at the disk level.
        </li>
        <li>
          <strong>Who can read it.</strong> Only the two participants
          in a thread (the provider and the client). Other users of
          the Registry &mdash; including other providers, other
          clients, and unrelated buyers/sellers &mdash; have no way
          to view your messages. Armstrong staff can view a thread
          only when (a)&nbsp;both sides have consented in writing,
          (b)&nbsp;one side opens a dispute that requires us to
          review the conversation, or (c)&nbsp;we are compelled to
          do so by law (e.g. a valid subpoena). When staff access a
          thread, the access is logged.
        </li>
        <li>
          <strong>Push delivery.</strong> If you have web-push
          notifications enabled (Settings &rarr; Notifications), a
          short preview of each incoming message (sender name plus
          up to ~120 characters of the body) is delivered to your
          device's notification shade through your browser's native
          push service (Apple Push Notification service, Firebase
          Cloud Messaging, or Mozilla autopush, depending on your
          browser). These third parties do not retain message
          contents. You can disable push at any time without losing
          access to the messages themselves (they remain in your
          inbox).
        </li>
        <li>
          <strong>Automated PII detection.</strong> When you compose
          a message, our system runs a local-only regular-expression
          scan that flags US-format phone numbers and email
          addresses. If the scan triggers, you see a soft warning
          encouraging you to keep the conversation on Armstrong
          &mdash; you can still send the message; the warning does
          not block you. A boolean flag (<code>pii_warning</code>) is
          stored on the message so we can show the same warning on
          the receiver's side and improve the experience over time.
          We do not extract or index the detected values themselves.
        </li>
        <li>
          <strong>Moderation rights.</strong> Armstrong K9 Registry is
          a venue and does not pre-screen messages. We may, however,
          (i)&nbsp;temporarily quarantine a thread that has been
          reported via the in-app report flow, (ii)&nbsp;remove
          specific messages or attachments that violate our content
          policy (illegal content, harassment, fraud, etc.), and
          (iii)&nbsp;suspend the sending or receiving account in
          serious cases. We do not edit message bodies; redaction is
          deletion-of-message-only.
        </li>
        <li>
          <strong>Retention.</strong> Threads are retained while
          both participating accounts are active. If either account
          is deleted, that side of the thread becomes inaccessible
          to the deleted user; the remaining side retains the
          thread (with the deleted user shown as "User removed") for
          90&nbsp;days, after which the entire thread and its
          attachments are purged. Threads explicitly archived by
          both sides are purged 1&nbsp;year after the last activity.
        </li>
        <li>
          <strong>Export &amp; deletion.</strong> You can export your
          entire message history (both sides of every thread you've
          participated in) from Settings &rarr; Data &amp; Privacy
          &rarr; Export. You can request deletion of a specific
          thread, your half of a thread, or every thread by emailing{" "}
          <a href="mailto:privacy@armstrongk9registry.com">
            privacy@armstrongk9registry.com
          </a>
          ; deletion is honored within 30&nbsp;days unless the thread
          is the subject of an open dispute, in which case it is
          preserved until the dispute is closed (then deleted in the
          next purge cycle).
        </li>
        <li>
          <strong>Off-platform contact at your own risk.</strong> If
          you choose to share contact information with a provider
          (phone, personal email, social handles) and move the
          conversation off Armstrong, Armstrong cannot assist with
          disputes, cancellations, or refunds for service rendered
          through that off-platform channel. Keeping conversations on
          the Registry preserves your paper trail.
        </li>
      </ul>
      <h3>2.4b Other communications</h3>
      <ul>
        <li>
          Email content we send via Resend (transactional notifications,
          certificate attachments, transfer invites).
        </li>
        <li>
          Marketing announcements (e.g. feature-launch blasts) are delivered
          both via in-app inbox and email. You can opt out of broadcasts at
          any time from Settings &rarr; Notification preferences; opting out is
          honored before we send.
        </li>
        <li>
          <strong>Lifecycle emails about your dog(s).</strong> If you own a
          registered dog on the platform, we send a small set of
          date-triggered emails: an annual <em>birthday</em> note, an annual{" "}
          <em>Gotcha Day</em> anniversary note (one year after a transfer),
          a one-time 30-day <em>buyer check-in</em> after you receive a dog
          (which invites you to leave a verified review for the seller),
          and &mdash; if you bought a puppy &mdash; a six-lesson{" "}
          <em>first-puppy school drip</em> on days 1, 3, 5, 7, 14, and 30
          after homecoming. Every lifecycle email contains a one-click
          unsubscribe link (CAN-SPAM Section 5(a)(3) compliant) AND can be
          managed globally from Settings &rarr; Notifications or
          per-dog from each dog's Ownership tab. Transactional emails
          (purchases, password resets, transfer requests, certificate
          delivery) cannot be unsubscribed from because they are required
          to operate the service. The lifecycle email content is
          educational only; it is not veterinary, legal, or tax advice.
        </li>
      </ul>

      <h3>2.5 Live video tours</h3>
      <ul>
        <li>
          When you book or join a 15-minute live video tour, the call is
          delivered by our sub-processor Daily.co. The audio/video stream is
          end-to-end transient &mdash; we do not record or store it. Daily.co
          retains diagnostic logs (timestamps, room id, participant
          identifiers) per their published DPA. See our{" "}
          <Link to="/sub-processors">Sub-processors</Link> page.
        </li>
      </ul>

      <h3>2.6 Two-factor authentication (TOTP)</h3>
      <ul>
        <li>
          If you enable 2FA on your account, we store an encrypted TOTP
          shared secret alongside your account. The secret is used solely to
          verify codes you enter at sign-in or before high-value actions
          (transfers, escrow releases). Disable 2FA at any time from your
          account settings to delete the stored secret.
        </li>
      </ul>

      <h3>2.7 Technical &amp; analytics</h3>
      <ul>
        <li>IP address, user-agent, pages viewed, referrer.</li>
        <li>
          Performance and error telemetry (only when our error reporter is
          enabled &mdash; never form-input contents).
        </li>
        <li>Analytics events (when you accept the cookie banner).</li>
      </ul>

      <h3>2.8 Web push notification subscriptions</h3>
      <p>
        If you enable browser push notifications (for example, to receive
        ~24-hour reminders before calendar events, transport-status updates,
        escrow milestones, or buyer inquiries), we store the subscription
        details required to deliver them.
      </p>
      <ul>
        <li>
          The push endpoint URL issued by your browser&rsquo;s push service
          (Apple Push for Safari, FCM for Chrome/Edge, Mozilla autopush for
          Firefox), the public application key, and a per-subscription
          identifier.
        </li>
        <li>
          The user-agent and approximate device label (so you can revoke a
          single device from your settings without revoking all of them).
        </li>
        <li>
          We do not store the contents of notifications beyond what is
          needed to deduplicate sends (e.g., calendar event id and a
          fired-at timestamp) so you don&rsquo;t get the same reminder
          twice.
        </li>
        <li>
          You can revoke push consent at any time from Settings →
          Notifications or by removing the site from your browser&rsquo;s
          notification permissions. Revoking consent triggers immediate
          deletion of the subscription record.
        </li>
        <li>
          Reminder delivery is best-effort. Browser push services may delay
          or drop notifications. Do not rely on push alerts as your sole
          reminder for time-critical events.
        </li>
      </ul>

      <h3>2.9 AI-assisted features</h3>
      <p>
        Some features on the Registry call third-party large language models
        to generate text on your behalf &mdash; for example, the AI
        marketing-pack generator (admin-only), the kennel-bio assistant, and
        any feature labeled &ldquo;AI&rdquo; or &ldquo;AI-generated.&rdquo;
      </p>
      <ul>
        <li>
          <strong>What we send to the LLM.</strong> Only the prompt plus the
          context required to answer it. For the marketing generator this is
          aggregate platform statistics (counts of kennels, dogs, breeds,
          completed transports) and the most recent feature changelog
          headlines &mdash; not individual user records. For the kennel-bio
          assistant this is the kennel&rsquo;s own description and the
          breeder&rsquo;s selections. We do not include other users&rsquo;
          personal data in prompts.
        </li>
        <li>
          <strong>Provider.</strong> We currently route AI calls to
          Anthropic&rsquo;s Claude family via the Emergent LLM Key managed by
          Emergent. The current vendor list, regions, and DPA links are on
          our <Link to="/sub-processors">Sub-processors</Link> page.
        </li>
        <li>
          <strong>No training on your content.</strong> Our LLM access is
          configured so that prompts and outputs are not retained by the
          provider for model training. If this ever changes we will update
          the Sub-processors page and notify you before any retention change
          takes effect.
        </li>
        <li>
          <strong>Output is probabilistic, not authoritative.</strong>{" "}
          AI-generated text may contain factual errors. You are responsible
          for reviewing any AI-generated content before publishing or relying
          on it. See the corresponding section in our{" "}
          <Link to="/terms">Terms of Service</Link> for the warranty
          disclaimer.
        </li>
      </ul>

      <h3>2.10 Calendar &amp; reproductive-health data</h3>
      <p>
        The personal calendar may store sensitive breeding-related health
        data &mdash; heat-cycle dates, breeding events, predicted whelping
        dates, vet appointments, and any free-text notes you choose to
        attach. This data is treated with the same protections as the rest
        of your account: stored only on the Registry, encrypted in transit,
        never shared with advertising networks, and never sold. You can
        export it via your data-export request or delete it at any time from
        the calendar interface.
      </p>

      <h3>2.11 DNA panel reports &amp; genetic data</h3>
      <p>
        When you purchase a <em>DNA-Verified Pedigree Registration</em>{" "}
        ($75 add-on per dog) you may upload a third-party DNA panel report
        (Embark, Wisdom Panel, VGL, or equivalent). This is genetic data
        about your dog, not about a human, and is therefore not covered by
        GINA in the United States. We still treat it as sensitive:
      </p>
      <ul>
        <li>
          <strong>What we collect.</strong> The PDF or image you upload;
          metadata you provide (file name, optional description);
          our administrator&apos;s written review notes; and the
          approve / request-changes / reject decision.
        </li>
        <li>
          <strong>Who can see it.</strong> Only the dog&apos;s registered
          owner (you) and a small set of administrators with reviewer
          permissions. Reviewers see your uploaded files when assessing
          the parentage claim. Files are not shown on any public page.
        </li>
        <li>
          <strong>What we do with it.</strong> We use the report solely
          to verify your parentage claim before flipping the public
          DNA-Verified badge on your dog&apos;s profile. We do not
          re-sell, license, or share the report or its underlying genetic
          data with third parties, advertisers, insurers, or veterinary
          networks.
        </li>
        <li>
          <strong>Important — what DNA-Verified does and does not mean.</strong>
          The badge means an Armstrong K9 Registry administrator reviewed a
          third-party DNA panel report you submitted and found the parentage
          claim consistent with that report. <em>We do not independently
          lab-test your dog.</em> We are not a genetics laboratory and we
          cannot certify or guarantee the underlying lab&apos;s results.
        </li>
        <li>
          <strong>Retention.</strong> Approved DNA reports are retained for
          the life of the dog&apos;s registration on the platform so we can
          re-affirm the badge during ownership transfers and audit requests.
          Rejected / withdrawn reports are deleted within 90 days of the
          final decision. You can request earlier deletion at any time via
          the contact channels below; deletion will also revoke the
          DNA-Verified badge.
        </li>
        <li>
          <strong>Refunds &amp; revocation.</strong> If you request a refund
          of the $75 verification fee, your DNA-Verified badge is
          automatically removed within minutes of the refund clearing.
        </li>
      </ul>

      <h3>2.12 Referral attribution</h3>
      <ul>
        <li>
          <strong>What we collect.</strong> When you generate a referral
          code, we store an 8-character alphanumeric code linked to your
          user id. When someone signs up using your code (e.g. via{" "}
          <code>/register?ref=CODE</code>), we store on the new
          user&rsquo;s row: the referrer&rsquo;s user id
          (<code>referred_by_user_id</code>), the code used
          (<code>referred_by_code</code>), and the timestamp.
        </li>
        <li>
          <strong>Why.</strong> Analytics for the breeder-onboarding
          program; attribution if and when we issue referral credit;
          fraud prevention against self-referral and code abuse.
        </li>
        <li>
          <strong>Visibility.</strong> Only you (the referrer) see your
          own referral stats. We never expose referred users&rsquo;
          identities to other referrers beyond the public account name
          they chose at signup.
        </li>
      </ul>

      <h3>2.13 &ldquo;Bring your dog home&rdquo; tracker (buyers)</h3>
      <ul>
        <li>
          <strong>What we collect.</strong> When you complete an escrow
          purchase, we create a tracker row recording: the escrow id,
          the dog id, the seller, the seven milestone keys (paid in
          full, contract signed, microchip registered, vet check,
          travel arranged, picked up, first week home), the timestamp
          each milestone was marked complete, and any optional buyer
          notes you add (up to 600 characters per milestone).
        </li>
        <li>
          <strong>Why.</strong> So you can see your progress through
          the take-home journey on your Dashboard; so we can time the
          Dog Log subscription nudge to the moment you mark &ldquo;first
          week home&rdquo;.
        </li>
        <li>
          <strong>Visibility.</strong> Trackers are private to you.
          The seller does not see your milestone progress or your
          private notes.
        </li>
      </ul>

      <h3 id="live-walk-gps">2.14 Live Walk GPS data (walks &amp; sits)</h3>
      <ul>
        <li>
          <strong>What we collect.</strong> When a service provider
          (walker or sitter) and you (the customer) <em>both</em>
          opt in to Live Walk GPS for a specific booking, and the
          provider then starts a live session on that booking, our
          servers receive a stream of approximate latitude / longitude
          points + a timestamp per point, transmitted from the
          provider&rsquo;s phone every few seconds for the duration of
          that single session. We also record the booking id, the
          provider&rsquo;s user id, your user id (as the consenting
          viewer), the start time, the end time, and the session
          duration.
        </li>
        <li>
          <strong>Why.</strong> The sole purpose is to render the live
          map you see in your authenticated{" "}
          <code>/my-bookings</code> page while the walk or sit is
          actively in progress. We do not use this data for
          advertising, profiling, route analytics, traffic studies,
          ranking, or any secondary purpose.
        </li>
        <li>
          <strong>Lawful basis.</strong> <strong>Bilateral explicit
          consent.</strong> You opt in on the booking checkout
          (the <code>live_walk_consent</code> field) and the provider
          separately initiates each session from their device. Either
          party may revoke at any moment from inside the app and the
          stream stops within seconds.
        </li>
        <li>
          <strong>Retention &mdash; 24-hour hard delete.</strong> Raw
          GPS points are discarded server-side <em>immediately</em>
          when the session ends or is revoked. A summary record
          (total distance, total duration, approximate start and end
          locations snapped to roughly the nearest 110 metres for
          home-address obfuscation) is kept only for 24 hours from
          end-of-session, then permanently deleted. We do not back up
          GPS data, we do not retain it cold, and there is no
          &ldquo;export historical walks&rdquo; feature anywhere in
          the product. Once it&rsquo;s gone it&rsquo;s gone.
        </li>
        <li>
          <strong>Recipients.</strong> You (the consenting customer,
          in-app only) and Armstrong K9 Registry (as the storage
          operator for the live session and the 24-hour summary
          window). The provider sees their own raw stream on their
          device. <strong>No advertisers, no data brokers, no
          analytics vendors, no map-tile partners</strong> receive
          your GPS data; map tiles are rendered with anonymous viewport
          requests that do not include the dog&rsquo;s coordinates.
        </li>
        <li>
          <strong>No public share links.</strong> The live map is
          accessible only from your authenticated{" "}
          <code>/my-bookings</code> page. There is no public URL, no
          SMS link, no email link, no embed, and no QR code. Location
          data never traverses any out-of-band channel.
        </li>
        <li>
          <strong>Your rights.</strong> You can (a) decline GPS at
          booking and the booking still works normally; (b) revoke
          mid-session and the stream stops within seconds; (c)
          request access or deletion of any GPS-related record (the
          24-hour summary) via the standard data-export and account-
          deletion flows in §7. Requests for raw points older than the
          retention window cannot be honored because we no longer
          have the data.
        </li>
        <li>
          <strong>Children.</strong> GPS is never collected from
          users under 18. The Platform is 18+ as required by §8;
          minors cannot hold accounts that book services or initiate
          walks.
        </li>
        <li>
          <strong>Not a safety system.</strong> Live Walk GPS is a
          passive sharing tool between two consenting users.
          Armstrong does not monitor the live feed, does not alert
          anyone if the provider stops transmitting or leaves a
          geofence, and does not guarantee accuracy or continuity of
          the GPS signal. Reliance is at your own risk; this
          treatment is described more fully in §5d-bis of the{" "}
          <Link to="/terms">Terms of Service</Link>.
        </li>
      </ul>

      <h3 id="zoomie-time-data" data-testid="privacy-zoomie-time-section">
        2.15 Zoomie Time field rentals (hosts &amp; guests)
      </h3>
      <ul>
        <li>
          <strong>Host listing data.</strong> When you list a private
          field for hourly rental we collect the field&rsquo;s street
          address, city/state, acreage, fencing status, amenities,
          house rules, photos, hourly rate, and your availability
          windows, plus your Stripe Connect payout identifiers
          (handled by Stripe &mdash; see &sect;2.3).
        </li>
        <li>
          <strong>Address masking.</strong> Your exact address is
          never shown on public listings &mdash; browsers see only an
          approximate area. The full address is disclosed only to a
          guest with a confirmed, paid booking, and only for that
          booking.
        </li>
        <li>
          <strong>Guest booking data.</strong> For each Zoomie Time
          booking we record the booking time slot, the registered
          dogs you selected to attend, the amount paid, and your
          acceptance of the field-use liability waiver (with a
          timestamp), which we retain as evidence of consent for as
          long as legally required.
        </li>
        <li>
          <strong>No device GPS.</strong> Zoomie Time does not
          collect location from your device. The only location data
          involved is the address the Host types into their listing.
        </li>
        <li>
          <strong>Recipients.</strong> The Host sees the guest&rsquo;s
          name and the names/breeds of the dogs on the booking so
          they know who is visiting. Guests see the Host&rsquo;s
          listing details and, post-booking, the field address. No
          third parties receive this data other than Stripe for
          payment processing.
        </li>
      </ul>

      <h2>3. Why we use it (legal bases)</h2>
      <ul>
        <li>
          <strong>Contractual necessity</strong> &mdash; to provide the
          Registry services you signed up for: storing your dogs, processing
          payments, delivering notifications, supporting transfers and
          disputes.
        </li>
        <li>
          <strong>Legal obligation</strong> &mdash; to comply with tax,
          anti-money laundering, sanctions screening, and
          animal-welfare-disclosure laws.
        </li>
        <li>
          <strong>Legitimate interest</strong> &mdash; security monitoring,
          fraud prevention, abuse detection, product analytics (with consent
          in the EU/UK).
        </li>
        <li>
          <strong>Consent</strong> &mdash; analytics cookies, marketing
          emails, web push notifications, and AI-assisted generators
          (marketing pack, kennel bio, etc.). You can withdraw consent at
          any time.
        </li>
      </ul>

      <h2>4. Who we share it with</h2>
      <p>
        We share personal data only with vendors that act as our processors,
        and only the minimum needed for the function below. The full list
        with regions and DPA links is on our{" "}
        <Link to="/sub-processors">Sub-processors</Link> page.
      </p>
      <ul>
        <li>
          <strong>Stripe</strong> &mdash; payments, payouts, KYC, Identity
          verification.
        </li>
        <li><strong>Resend</strong> &mdash; transactional email delivery.</li>
        <li>
          <strong>MongoDB Atlas</strong> &mdash; primary database storage.
        </li>
        <li>
          <strong>Emergent</strong> &mdash; application hosting, object
          storage, Google sign-in, and the LLM Key gateway that routes AI
          prompts to the underlying providers below.
        </li>
        <li>
          <strong>Anthropic (Claude)</strong> &mdash; AI text generation for
          the marketing-pack generator and kennel-bio assistant. We never
          include other users&rsquo; personal data in prompts; outputs are
          not retained for training.
        </li>
        <li>
          <strong>Google</strong> &mdash; only when you choose Google
          sign-in.
        </li>
        <li>
          <strong>Daily.co</strong> &mdash; short-lived video tour rooms
          (only when you book a video tour).
        </li>
        <li>
          <strong>Cloudflare</strong> &mdash; Turnstile bot/abuse protection
          on public forms.
        </li>
        <li>
          <strong>Browser push services</strong> &mdash; Apple Push
          (Safari/iOS), FCM (Chrome/Edge/Android), Mozilla autopush
          (Firefox). Only the push endpoint your browser issues is shared,
          and only when you have enabled web push notifications.
        </li>
        <li>
          <strong>Apple (In-App Purchase)</strong> &mdash; when you buy a
          subscription inside the iOS app, Apple processes the payment
          against your Apple&nbsp;ID. Your card details stay with Apple and
          are never shared with us; we receive only transaction identifiers
          and subscription status.
        </li>
        <li>
          <strong>RevenueCat</strong> &mdash; validates Apple In-App
          Purchase receipts and tells our backend which subscription your
          account holds. RevenueCat receives an internal user ID and Apple
          transaction data only &mdash; never your name, email, or payment
          details.
        </li>
      </ul>
      <p>
        We do not sell personal data, and we do not share it with advertising
        networks. We may disclose data when required by valid legal process
        or to protect the safety of users, but we will fight overbroad
        requests.
      </p>

      <h2>5. Public-facing data</h2>
      <p>
        The Registry is partially public by design &mdash; kennel pages
        (<code>/k/&lt;slug&gt;</code>), individual dog profiles
        (<code>/d/&lt;id&gt;</code>), marketplace listings, and the events
        catalog are accessible to anyone, including non-account holders.
        These pages may show:
      </p>
      <ul>
        <li>
          Dog name, breed, sex, DOB, color, registration number, photos,
          titles, pedigree (sire/dam names).
        </li>
        <li>
          Public kennel name, bio, photos, slug, dog count, badges
          (Verified, Elite Verified).
        </li>
        <li>
          Marketplace asking price, location (when provided), description,
          listing tier.
        </li>
      </ul>
      <p>
        Microchip numbers, health records, ownership history, and contact
        information are <strong>not</strong> exposed on public pages.
      </p>

      <h2>6. Cookies</h2>
      <ul>
        <li>
          <strong>Essential cookies:</strong> authentication tokens (JWT,
          session), referral code capture, billing persona toggle. These
          cannot be disabled.
        </li>
        <li>
          <strong>Analytics cookies:</strong> PostHog event tracking. Loaded
          only after you accept the cookie banner.
        </li>
      </ul>
      <p>
        You can change your choice at any time by clearing site data in your
        browser; the banner will reappear on your next visit.
      </p>

      <h2>7. Your rights</h2>

      <h3>7.1 Anyone</h3>
      <ul>
        <li>
          <strong>Access</strong> &mdash; view all data we hold about you on
          Settings → Privacy.
        </li>
        <li>
          <strong>Export</strong> &mdash; download a JSON copy via Settings →
          Privacy → Export my data.
        </li>
        <li>
          <strong>Correct</strong> &mdash; edit your profile, dogs, and
          litters at any time.
        </li>
        <li>
          <strong>Delete</strong> &mdash; Settings → Privacy → Deactivate. We
          retain anonymized payment records for 7 years to satisfy tax law.
        </li>
      </ul>

      <h3>7.2 EU/UK residents (GDPR)</h3>
      <p>
        You also have rights to restrict processing, object to legitimate-
        interest processing, and lodge a complaint with your local
        supervisory authority. Contact{" "}
        <a href="mailto:privacy@armstrongk9registry.com">
          privacy@armstrongk9registry.com
        </a>
        .
      </p>

      <h3>7.3 California residents (CCPA / CPRA)</h3>
      <p>
        You have the right to know, delete, correct, and limit use of your
        personal information; the right to opt-out of &ldquo;sale&rdquo; or
        &ldquo;share&rdquo; of personal information; and the right not to be
        discriminated against for exercising these rights. We do not sell or
        share personal data in the meaning of CCPA. To verify a deletion or
        access request we will ask you to confirm via the email on file.
      </p>

      <h2>8. Children</h2>
      <p>
        The Registry is not directed at children under 13 and we do not
        knowingly collect personal information from anyone under 13. The
        registration form requires you to confirm you are at least 18. If
        you believe a minor has registered, contact us and we will delete
        the account.
      </p>

      <h2>9. Data retention</h2>
      <ul>
        <li>
          <strong>Account &amp; pedigree records:</strong> kept while the
          account is active. After deactivation, retained for 30 days then
          anonymized.
        </li>
        <li>
          <strong>Payment records:</strong> retained for 7 years for
          tax/audit purposes (anonymized after deactivation).
        </li>
        <li>
          <strong>Inbox &amp; chat messages:</strong> retained while the
          account is active; purged 30 days after deletion. Provider
          &harr; Client direct-message threads follow the specific
          retention schedule in &sect;2.4a above.
        </li>
        <li>
          <strong>Pedigree-only ancestor records (added by other users):</strong>{" "}
          retained indefinitely as part of the public registry.
        </li>
        <li><strong>Server logs:</strong> 90 days.</li>
        <li>
          <strong>Live Walk GPS:</strong> raw points discarded
          immediately when a session ends; 24-hour summary retained
          for 24 hours then hard-deleted. No backups, no cold
          retention.
        </li>
      </ul>

      <h2>10. Security</h2>
      <p>
        Passwords are hashed with bcrypt. Sessions are signed JWTs (7-day
        expiry) or revocable session tokens stored httpOnly. All traffic is
        TLS-encrypted. We follow the controls described on our Security
        Measures page.
      </p>

      <h2>11. International transfers</h2>
      <p>
        Our infrastructure is in the United States. EU/UK personal data
        crosses the border under the EU-US Data Privacy Framework and
        Standard Contractual Clauses. Each sub-processor has its own SCC
        framework; see our <Link to="/sub-processors">Sub-processors</Link>{" "}
        page.
      </p>

      <h2>12. Changes</h2>
      <p>
        Material changes to this policy will be announced via email and an
        in-app banner at least 30 days before they take effect. The
        &ldquo;Last updated&rdquo; date at the top of this page reflects the
        latest revision.
      </p>

      <h2>13. Contact</h2>
      <p>
        Privacy questions, requests, and complaints:{" "}
        <a href="mailto:privacy@armstrongk9registry.com">
          privacy@armstrongk9registry.com
        </a>
      </p>
    </LegalLayout>
  );
}
